/**
 * Read an "Add people and tiers" sheet (CSV or tab-separated, as Excel
 * saves it) into upload rows. Headers are matched case-insensitively, with
 * spaces or underscores: first_name, last_name, email, phone,
 * postal_address (or address), company_name (or company, or the contacts
 * export's company_name_hint), job_title, tier. Everything else is ignored.
 *
 * Only the reading lives here: whether a row can be used (a tier, a name or
 * an email, a valid email, a known tier) is decided by the server's preview,
 * which the confirm re-runs, so the two cannot disagree.
 */

import type { PeopleUploadRow } from "../data/api-types";

const COLUMN_ALIASES: Record<keyof PeopleUploadRow, string[]> = {
  first_name: ["first_name", "first name", "firstname"],
  last_name: ["last_name", "last name", "lastname", "surname"],
  email: ["email", "email address", "email_address"],
  phone: ["phone", "telephone", "phone number"],
  postal_address: ["postal_address", "postal address", "address"],
  company_name: [
    "company_name",
    "company name",
    "company",
    "company_name_hint",
  ],
  job_title: ["job_title", "job title", "title"],
  tier_name: ["tier", "tier_name", "tier name"],
};

export interface CampaignPeopleCsv {
  rows: PeopleUploadRow[];
  /** Problems with the file as a whole (no rows, no tier column). */
  errors: string[];
}

export function parseCampaignPeopleCsv(text: string): CampaignPeopleCsv {
  const records = splitRecords(text.replace(/^\uFEFF/, ""));
  if (records.length < 2) {
    return { rows: [], errors: ["The file has no rows of people."] };
  }
  const delimiter = records[0].includes("\t") ? "\t" : ",";
  const header = splitFields(records[0], delimiter).map((h) =>
    h.trim().toLowerCase(),
  );
  const columnOf = Object.fromEntries(
    (Object.keys(COLUMN_ALIASES) as (keyof PeopleUploadRow)[]).map((field) => [
      field,
      header.findIndex((h) => COLUMN_ALIASES[field].includes(h)),
    ]),
  ) as Record<keyof PeopleUploadRow, number>;
  if (columnOf.tier_name < 0) {
    return {
      rows: [],
      errors: ["The file needs a tier column saying each person's tier."],
    };
  }

  const rows: PeopleUploadRow[] = [];
  for (const record of records.slice(1)) {
    const fields = splitFields(record, delimiter);
    if (fields.every((f) => f.trim() === "")) continue;
    const row: PeopleUploadRow = {};
    for (const field of Object.keys(columnOf) as (keyof PeopleUploadRow)[]) {
      const index = columnOf[field];
      const value = index >= 0 ? (fields[index] ?? "").trim() : "";
      if (value) row[field] = value;
    }
    rows.push(row);
  }
  return { rows, errors: [] };
}

/** Split into records on line breaks outside quotes, so a quoted address
 *  that spans lines stays one record. */
function splitRecords(text: string): string[] {
  const records: string[] = [];
  let current = "";
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (ch === '"') inQuotes = !inQuotes;
    if (!inQuotes && (ch === "\n" || ch === "\r")) {
      if (ch === "\r" && text[i + 1] === "\n") i++;
      if (current.trim() !== "") records.push(current);
      current = "";
      continue;
    }
    current += ch;
  }
  if (current.trim() !== "") records.push(current);
  return records;
}

/** Split one record into fields, honouring quotes ("" is a literal quote). */
function splitFields(record: string, delimiter: string): string[] {
  const fields: string[] = [];
  let current = "";
  let inQuotes = false;
  for (let i = 0; i < record.length; i++) {
    const ch = record[i];
    if (inQuotes) {
      if (ch === '"' && record[i + 1] === '"') {
        current += '"';
        i++;
      } else if (ch === '"') {
        inQuotes = false;
      } else {
        current += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === delimiter) {
      fields.push(current);
      current = "";
    } else {
      current += ch;
    }
  }
  fields.push(current);
  return fields;
}
